import { useEffect, useState, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import moment from 'moment'
import { viewSettlementDetails, settleBooking, settleAllBookings } from '../../slice/settlementSlice'

// ─────────────────────────────────────────────────────────────────────────────
// Settle Modal — handles single, pack_5, pack_10
// ─────────────────────────────────────────────────────────────────────────────
const SettleModal = ({ booking, onClose, onConfirm, loading }) => {

    const isSingle = booking.session_type === 'single'
    const maxSessions = booking.sessions_pending_payout ?? 0
    const perSessionAmt = parseFloat(booking.price_per_session ?? 0)
    const perSessionNetPrice = parseFloat(booking.per_session_net_price ?? 0)

    const [sessionCount, setSessionCount] = useState(isSingle ? 1 : 1)
    const [paymentMode, setPaymentMode] = useState('bank_transfer')
    const [referenceNumber, setReferenceNumber] = useState('')
    const [paidAt, setPaidAt] = useState(moment().format('YYYY-MM-DDTHH:mm'))
    const [notes, setNotes] = useState('')

    const amountToSettle = isSingle
        ? parseFloat(booking.balance_due ?? 0)
        : parseFloat((sessionCount * perSessionNetPrice).toFixed(2))

    const fmt = (val) =>
        val != null && val !== ''
            ? `AED ${parseFloat(val).toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            : '—'

    const handleConfirm = () => {
        if (maxSessions === 0) return
        onConfirm({
            booking_order_id: booking.booking_order_id,
            sessions_settled: isSingle ? 1 : sessionCount,
            amount_settled: amountToSettle,
            payment_mode: paymentMode,
            reference_number: referenceNumber.trim() || null,
            paid_at: paidAt ? new Date(paidAt).toISOString() : new Date().toISOString(),
            notes: notes.trim() || null,
        })
    }

    return (
        <div
            style={{
                position: 'fixed', inset: 0, zIndex: 1055,
                background: 'rgba(0,0,0,0.45)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '1rem',
            }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
        >
            <div style={{
                background: '#fff', borderRadius: 16, width: '100%', maxWidth: 520,
                boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
                maxHeight: '90vh', overflowY: 'auto',
            }}>
                {/* Modal Header */}
                <div style={{
                    padding: '20px 24px 16px', borderBottom: '1px solid #f0f0f0',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                    <div>
                        <h5 style={{ margin: 0, fontWeight: 600, fontSize: 16 }}>Settle Payment</h5>
                        <span style={{ fontSize: 12, color: '#6c757d' }}>
                            Order #{booking.order_id || booking.booking_order_id}
                            &nbsp;·&nbsp;
                            <span style={{ textTransform: 'capitalize' }}>
                                {(booking.session_type || '').replace(/_/g, ' ')}
                            </span>
                        </span>
                    </div>
                    <button
                        onClick={onClose}
                        style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6c757d', lineHeight: 1 }}
                    >×</button>
                </div>

                {/* Modal Body */}
                <div style={{ padding: '20px 24px' }}>

                    {/* Session summary strip */}
                    <div style={{
                        background: '#f8f9fa', borderRadius: 10, padding: '12px 16px',
                        display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 20, fontSize: 13,
                    }}>
                        <div>
                            <div style={{ color: '#6c757d', fontSize: 11, marginBottom: 2 }}>Completed</div>
                            <strong style={{ color: '#059669' }}>{booking.sessions_completed ?? 0}</strong>
                        </div>
                        <div>
                            <div style={{ color: '#6c757d', fontSize: 11, marginBottom: 2 }}>Already Settled</div>
                            <strong style={{ color: '#1d4ed8' }}>{booking.sessions_settled ?? 0}</strong>
                        </div>
                        <div>
                            <div style={{ color: '#6c757d', fontSize: 11, marginBottom: 2 }}>Available to Settle</div>
                            <strong style={{ color: '#f59e0b' }}>{maxSessions}</strong>
                        </div>
                        <div>
                            <div style={{ color: '#6c757d', fontSize: 11, marginBottom: 2 }}>Per Session</div>
                            <strong>{fmt(perSessionAmt)}</strong>
                        </div>
                    </div>

                    {maxSessions === 0 ? (
                        <div style={{
                            background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8,
                            padding: '12px 16px', color: '#065f46', fontSize: 13, marginBottom: 16,
                            display: 'flex', alignItems: 'center', gap: 8,
                        }}>
                            <i className="fe-check-circle" style={{ fontSize: 16 }} />
                            All completed sessions for this booking are already settled.
                        </div>
                    ) : (
                        <>
                            {/* Sessions to settle — only show picker for packs */}
                            {!isSingle && (
                                <div style={{ marginBottom: 16 }}>
                                    <label style={{ fontSize: 13, fontWeight: 500, marginBottom: 6, display: 'block' }}>
                                        Sessions to settle
                                        <span style={{ fontWeight: 400, color: '#6c757d' }}> (max {maxSessions})</span>
                                    </label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        <input
                                            type="range"
                                            min={1}
                                            max={maxSessions}
                                            value={sessionCount}
                                            onChange={e => setSessionCount(Number(e.target.value))}
                                            style={{ flex: 1 }}
                                        />
                                        <input
                                            type="number"
                                            min={1}
                                            max={maxSessions}
                                            value={sessionCount}
                                            onChange={e => {
                                                const v = Math.min(Math.max(1, Number(e.target.value)), maxSessions)
                                                setSessionCount(v)
                                            }}
                                            style={{
                                                width: 64, textAlign: 'center', border: '1px solid #dee2e6',
                                                borderRadius: 6, padding: '4px 8px', fontSize: 14, fontWeight: 600,
                                            }}
                                        />
                                    </div>
                                    {/* Quick-pick buttons */}
                                    <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                                        {[1, Math.ceil(maxSessions / 2), maxSessions]
                                            .filter((v, i, a) => a.indexOf(v) === i && v >= 1)
                                            .map(v => (
                                                <button
                                                    key={v}
                                                    onClick={() => setSessionCount(v)}
                                                    style={{
                                                        padding: '3px 12px', fontSize: 12, borderRadius: 6, cursor: 'pointer',
                                                        border: '1px solid',
                                                        borderColor: sessionCount === v ? '#1d4ed8' : '#dee2e6',
                                                        background: sessionCount === v ? '#eff6ff' : '#fff',
                                                        color: sessionCount === v ? '#1d4ed8' : '#374151',
                                                        fontWeight: sessionCount === v ? 600 : 400,
                                                    }}
                                                >
                                                    {v === maxSessions ? `All (${v})` : v}
                                                </button>
                                            ))}
                                    </div>
                                </div>
                            )}

                            {/* Amount preview */}
                            <div style={{
                                background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8,
                                padding: '10px 16px', marginBottom: 16, display: 'flex',
                                justifyContent: 'space-between', alignItems: 'center',
                            }}>
                                <span style={{ fontSize: 13, color: '#1e40af' }}>
                                    Amount to settle {!isSingle && `(${sessionCount} session${sessionCount > 1 ? 's' : ''})`}
                                </span>
                                <strong style={{ fontSize: 18, color: '#1d4ed8' }}>{fmt(amountToSettle)}</strong>
                            </div>

                            {/* Payment mode */}
                            <div style={{ marginBottom: 14 }}>
                                <label style={{ fontSize: 13, fontWeight: 500, marginBottom: 6, display: 'block' }}>Payment mode</label>
                                <select
                                    value={paymentMode}
                                    onChange={e => setPaymentMode(e.target.value)}
                                    style={{
                                        width: '100%', border: '1px solid #dee2e6', borderRadius: 8,
                                        padding: '8px 12px', fontSize: 13,
                                    }}
                                >
                                    <option value="bank_transfer">Bank Transfer</option>
                                    <option value="cash">Cash</option>
                                    <option value="upi">UPI</option>
                                    <option value="cheque">Cheque</option>
                                    <option value="wallet">Wallet</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>

                            {/* Reference number */}
                            <div style={{ marginBottom: 14 }}>
                                <label style={{ fontSize: 13, fontWeight: 500, marginBottom: 6, display: 'block' }}>
                                    Reference number <span style={{ fontWeight: 400, color: '#6c757d' }}>(UTR / TXN ID)</span>
                                </label>
                                <input
                                    type="text"
                                    value={referenceNumber}
                                    onChange={e => setReferenceNumber(e.target.value)}
                                    placeholder="e.g. UTR112233 (optional)"
                                    style={{
                                        width: '100%', border: '1px solid #dee2e6', borderRadius: 8,
                                        padding: '8px 12px', fontSize: 13, boxSizing: 'border-box',
                                    }}
                                />
                            </div>

                            {/* Paid at */}
                            <div style={{ marginBottom: 14 }}>
                                <label style={{ fontSize: 13, fontWeight: 500, marginBottom: 6, display: 'block' }}>Paid at</label>
                                <input
                                    type="datetime-local"
                                    value={paidAt}
                                    onChange={e => setPaidAt(e.target.value)}
                                    style={{
                                        width: '100%', border: '1px solid #dee2e6', borderRadius: 8,
                                        padding: '8px 12px', fontSize: 13, boxSizing: 'border-box',
                                    }}
                                />
                            </div>

                            {/* Notes */}
                            <div style={{ marginBottom: 4 }}>
                                <label style={{ fontSize: 13, fontWeight: 500, marginBottom: 6, display: 'block' }}>
                                    Notes <span style={{ fontWeight: 400, color: '#6c757d' }}>(optional)</span>
                                </label>
                                <textarea
                                    value={notes}
                                    onChange={e => setNotes(e.target.value)}
                                    placeholder="Any remarks about this settlement..."
                                    rows={2}
                                    style={{
                                        width: '100%', border: '1px solid #dee2e6', borderRadius: 8,
                                        padding: '8px 12px', fontSize: 13, resize: 'vertical',
                                        boxSizing: 'border-box',
                                    }}
                                />
                            </div>
                        </>
                    )}
                </div>

                {/* Modal Footer */}
                <div style={{
                    padding: '14px 24px 20px',
                    display: 'flex', justifyContent: 'flex-end', gap: 10,
                    borderTop: '1px solid #f0f0f0',
                }}>
                    <button
                        onClick={onClose}
                        disabled={loading}
                        style={{
                            padding: '8px 20px', borderRadius: 8, border: '1px solid #dee2e6',
                            background: '#fff', color: '#374151', cursor: 'pointer', fontSize: 13,
                        }}
                    >
                        Cancel
                    </button>
                    {maxSessions > 0 && (
                        <button
                            onClick={handleConfirm}
                            disabled={loading}
                            style={{
                                padding: '8px 20px', borderRadius: 8, border: 'none',
                                background: loading ? '#93c5fd' : '#1d4ed8',
                                color: '#fff', cursor: loading ? 'not-allowed' : 'pointer',
                                fontSize: 13, fontWeight: 500,
                                display: 'flex', alignItems: 'center', gap: 6,
                            }}
                        >
                            {loading && (
                                <span style={{
                                    width: 14, height: 14, border: '2px solid #fff',
                                    borderTopColor: 'transparent', borderRadius: '50%',
                                    display: 'inline-block', animation: 'spin 0.7s linear infinite',
                                }} />
                            )}
                            {loading ? 'Processing...' : 'Confirm Settlement'}
                        </button>
                    )}
                </div>
            </div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    )
}

// ─────────────────────────────────────────────────────────────────────────────
// Settle All Confirmation Modal
// ─────────────────────────────────────────────────────────────────────────────
const SettleAllModal = ({ pendingBookings, onClose, onConfirm, loading }) => {

    const [paymentMode, setPaymentMode] = useState('bank_transfer')
    const [referenceNumber, setReferenceNumber] = useState('')
    const [paidAt, setPaidAt] = useState(moment().format('YYYY-MM-DDTHH:mm'))
    const [notes, setNotes] = useState('')

    const totalSessions = pendingBookings.reduce((s, b) => s + (b.sessions_pending_payout ?? 0), 0)
    const totalAmount = pendingBookings.reduce((s, b) => {
        const perSession = parseFloat(b.per_session_net_price ?? 0)
        const sessions = b.sessions_pending_payout ?? 0
        return s + perSession * sessions
    }, 0)

    const fmt = (val) => val != null
        ? `AED ${parseFloat(val).toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : '—'

    const handleConfirm = () => {
        const items = pendingBookings.map(b => ({
            booking_order_id: b.booking_order_id,
            sessions_settled: b.sessions_pending_payout,
            amount_settled: parseFloat((parseFloat(b.per_session_net_price ?? 0) * (b.sessions_pending_payout ?? 0)).toFixed(2)),
        }))
        onConfirm({
            items,
            payment_mode: paymentMode,
            reference_number: referenceNumber.trim() || null,
            paid_at: paidAt ? new Date(paidAt).toISOString() : new Date().toISOString(),
            notes: notes.trim() || null,
        })
    }

    return (
        <div
            style={{
                position: 'fixed', inset: 0, zIndex: 1055,
                background: 'rgba(0,0,0,0.45)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '1rem',
            }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
        >
            <div style={{
                background: '#fff', borderRadius: 16, width: '100%', maxWidth: 540,
                boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
                maxHeight: '90vh', overflowY: 'auto',
            }}>
                <div style={{
                    padding: '20px 24px 16px', borderBottom: '1px solid #f0f0f0',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                    <div>
                        <h5 style={{ margin: 0, fontWeight: 600, fontSize: 16 }}>Settle All Pending</h5>
                        <span style={{ fontSize: 12, color: '#6c757d' }}>
                            {pendingBookings.length} booking{pendingBookings.length > 1 ? 's' : ''} · {totalSessions} sessions
                        </span>
                    </div>
                    <button
                        onClick={onClose}
                        style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6c757d' }}
                    >×</button>
                </div>

                <div style={{ padding: '20px 24px' }}>
                    {/* Breakdown table */}
                    <div style={{ marginBottom: 18, maxHeight: 200, overflowY: 'auto' }}>
                        <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: '#f8f9fa' }}>
                                    {['Order', 'Type', 'Sessions', 'Amount'].map(h => (
                                        <th key={h} style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: '#6c757d', fontSize: 10, letterSpacing: '0.04em' }}>
                                            {h.toUpperCase()}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {pendingBookings.map((b, i) => {
                                    const amt = parseFloat(b.per_session_net_price ?? 0) * (b.sessions_pending_payout ?? 0)
                                    return (
                                        <tr key={i} style={{ borderTop: '1px solid #f0f0f0' }}>
                                            <td style={{ padding: '6px 10px', fontWeight: 500 }}>#{b.order_id || b.booking_order_id}</td>
                                            <td style={{ padding: '6px 10px', textTransform: 'capitalize', color: '#6c757d' }}>
                                                {(b.session_type || '').replace(/_/g, ' ')}
                                            </td>
                                            <td style={{ padding: '6px 10px' }}>{b.sessions_pending_payout}</td>
                                            <td style={{ padding: '6px 10px', color: '#059669', fontWeight: 500 }}>{fmt(amt)}</td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Total */}
                    <div style={{
                        background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8,
                        padding: '10px 16px', marginBottom: 18,
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    }}>
                        <span style={{ fontSize: 13, color: '#1e40af' }}>Total amount to settle</span>
                        <strong style={{ fontSize: 18, color: '#1d4ed8' }}>{fmt(totalAmount)}</strong>
                    </div>

                    {/* Payment mode */}
                    <div style={{ marginBottom: 14 }}>
                        <label style={{ fontSize: 13, fontWeight: 500, marginBottom: 6, display: 'block' }}>Payment mode</label>
                        <select
                            value={paymentMode}
                            onChange={e => setPaymentMode(e.target.value)}
                            style={{ width: '100%', border: '1px solid #dee2e6', borderRadius: 8, padding: '8px 12px', fontSize: 13 }}
                        >
                            <option value="bank_transfer">Bank Transfer</option>
                            <option value="cash">Cash</option>
                            <option value="upi">UPI</option>
                            <option value="cheque">Cheque</option>
                            <option value="wallet">Wallet</option>
                            <option value="other">Other</option>
                        </select>
                    </div>

                    <div style={{ marginBottom: 14 }}>
                        <label style={{ fontSize: 13, fontWeight: 500, marginBottom: 6, display: 'block' }}>
                            Reference number <span style={{ fontWeight: 400, color: '#6c757d' }}>(optional)</span>
                        </label>
                        <input
                            type="text"
                            value={referenceNumber}
                            onChange={e => setReferenceNumber(e.target.value)}
                            placeholder="e.g. UTR112233"
                            style={{ width: '100%', border: '1px solid #dee2e6', borderRadius: 8, padding: '8px 12px', fontSize: 13, boxSizing: 'border-box' }}
                        />
                    </div>

                    <div style={{ marginBottom: 14 }}>
                        <label style={{ fontSize: 13, fontWeight: 500, marginBottom: 6, display: 'block' }}>Paid at</label>
                        <input
                            type="datetime-local"
                            value={paidAt}
                            onChange={e => setPaidAt(e.target.value)}
                            style={{ width: '100%', border: '1px solid #dee2e6', borderRadius: 8, padding: '8px 12px', fontSize: 13, boxSizing: 'border-box' }}
                        />
                    </div>

                    <div>
                        <label style={{ fontSize: 13, fontWeight: 500, marginBottom: 6, display: 'block' }}>
                            Notes <span style={{ fontWeight: 400, color: '#6c757d' }}>(optional)</span>
                        </label>
                        <textarea
                            value={notes}
                            onChange={e => setNotes(e.target.value)}
                            placeholder="Bulk settlement remarks..."
                            rows={2}
                            style={{ width: '100%', border: '1px solid #dee2e6', borderRadius: 8, padding: '8px 12px', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
                        />
                    </div>
                </div>

                <div style={{
                    padding: '14px 24px 20px',
                    display: 'flex', justifyContent: 'flex-end', gap: 10,
                    borderTop: '1px solid #f0f0f0',
                }}>
                    <button
                        onClick={onClose}
                        disabled={loading}
                        style={{ padding: '8px 20px', borderRadius: 8, border: '1px solid #dee2e6', background: '#fff', color: '#374151', cursor: 'pointer', fontSize: 13 }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={loading}
                        style={{
                            padding: '8px 20px', borderRadius: 8, border: 'none',
                            background: loading ? '#6ee7b7' : '#059669',
                            color: '#fff', cursor: loading ? 'not-allowed' : 'pointer',
                            fontSize: 13, fontWeight: 500,
                            display: 'flex', alignItems: 'center', gap: 6,
                        }}
                    >
                        {loading && (
                            <span style={{
                                width: 14, height: 14, border: '2px solid #fff',
                                borderTopColor: 'transparent', borderRadius: '50%',
                                display: 'inline-block', animation: 'spin 0.7s linear infinite',
                            }} />
                        )}
                        {loading ? 'Settling...' : `Settle All — ${fmt(totalAmount)}`}
                    </button>
                </div>
            </div>
        </div>
    )
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────
const ViewSettlementDetail = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()

    const tutorId = location.state?.tutorId
    const { settlementDetail, loading, settleLoading } = useSelector(state => state.settlement)

    // Modal state
    const [settleModal, setSettleModal] = useState(null)   // booking row | null
    const [settleAllModal, setSettleAllModal] = useState(false)
    const [toast, setToast] = useState(null)               // { type: 'success'|'error', msg }

    const reload = useCallback(() => {
        if (tutorId) dispatch(viewSettlementDetails({ tutor_id: tutorId }))
    }, [tutorId, dispatch])

    useEffect(() => {
        if (!tutorId) { navigate('/settlement'); return }
        reload()
    }, [tutorId, reload, navigate])

    // Auto-dismiss toast
    useEffect(() => {
        if (!toast) return
        const t = setTimeout(() => setToast(null), 3500)
        return () => clearTimeout(t)
    }, [toast])

    // ── Helpers ───────────────────────────────────────────────────────────────
    const fmt = (val) =>
        val != null && val !== ''
            ? `AED ${parseFloat(val).toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            : '—'
    const fmtDate = (d) => (d ? moment(d).format('DD MMM YYYY, hh:mm A') : '—')
    const fmtShortDate = (d) => (d ? moment(d).format('DD MMM YYYY') : '—')

    const statusBadge = (status) => {
        switch ((status || '').toLowerCase()) {
            case 'settled': return { bg: '#d1fae5', color: '#065f46' }
            case 'pending': return { bg: '#fef3c7', color: '#92400e' }
            case 'cancelled': return { bg: '#fee2e2', color: '#991b1b' }
            case 'completed': return { bg: '#dbeafe', color: '#1e40af' }
            case 'paid': return { bg: '#d1fae5', color: '#065f46' }
            default: return { bg: '#f3f4f6', color: '#374151' }
        }
    }

    // ── Settle single booking ─────────────────────────────────────────────────
    const handleSettleConfirm = async (payload) => {
        try {
            await dispatch(settleBooking({
                tutor_id: tutorId,
                items: [payload],
                payment_mode: payload.payment_mode,
                reference_number: payload.reference_number,
                paid_at: payload.paid_at,
                notes: payload.notes,
            })).unwrap()
            setSettleModal(null)
            setToast({ type: 'success', msg: 'Settlement recorded successfully.' })
            reload()
        } catch (err) {
            setToast({ type: 'error', msg: err?.message || 'Settlement failed. Please try again.' })
        }
    }

    // ── Settle all bookings ───────────────────────────────────────────────────
    const handleSettleAllConfirm = async (payload) => {
        try {
            await dispatch(settleAllBookings({
                tutor_id: tutorId,
                ...payload,
            })).unwrap()
            setSettleAllModal(false)
            setToast({ type: 'success', msg: 'All pending sessions settled successfully.' })
            reload()
        } catch (err) {
            setToast({ type: 'error', msg: err?.message || 'Settle all failed. Please try again.' })
        }
    }

    // ── Loading ───────────────────────────────────────────────────────────────
    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
                <div className="spinner-border text-primary" role="status" />
            </div>
        )
    }

    if (!settlementDetail || !settlementDetail.tutor) {
        return (
            <div className="text-center py-5">
                <i className="fe-alert-circle" style={{ fontSize: 48, color: '#dc3545' }} />
                <h5 className="mt-3">No data found</h5>
                <button className="btn btn-primary mt-3" onClick={() => navigate('/settlement')}>
                    ← Back to Settlements
                </button>
            </div>
        )
    }

    const { tutor, summary, bookings_detail = [], settlements = [] } = settlementDetail
    const displayName = tutor.name || `${tutor.first_name || ''} ${tutor.last_name || ''}`.trim() || 'Unknown'
    const initials = displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    const settlementProgress = Math.min(Number(summary.settlement_progress) || 0, 100)
    const progressColor = settlementProgress >= 80 ? '#10b981' : settlementProgress >= 40 ? '#3b82f6' : '#f59e0b'

    // Bookings that still have sessions pending payout
    const pendingBookings = bookings_detail.filter(b => (b.sessions_pending_payout ?? 0) > 0)
    const hasAnyPending = pendingBookings.length > 0

    const scrollTableWrapper = { maxHeight: 380, overflowY: 'auto', overflowX: 'auto' }

    return (
        <div className="container-fluid px-4 py-4">

            {/* ── Toast ── */}
            {toast && (
                <div style={{
                    position: 'fixed', top: 20, right: 20, zIndex: 2000,
                    background: toast.type === 'success' ? '#ecfdf5' : '#fef2f2',
                    border: `1px solid ${toast.type === 'success' ? '#6ee7b7' : '#fca5a5'}`,
                    color: toast.type === 'success' ? '#065f46' : '#991b1b',
                    padding: '12px 20px', borderRadius: 10, fontSize: 13,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                    display: 'flex', alignItems: 'center', gap: 8,
                    maxWidth: 360,
                }}>
                    <i className={toast.type === 'success' ? 'fe-check-circle' : 'fe-x-circle'} style={{ fontSize: 16 }} />
                    {toast.msg}
                </div>
            )}

            {/* ── Header ── */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="fw-semibold mb-0">Settlement Detail</h4>
                <div style={{ display: 'flex', gap: 10 }}>
                    {hasAnyPending && (
                        <button
                            onClick={() => setSettleAllModal(true)}
                            style={{
                                padding: '8px 18px', borderRadius: 8, border: 'none',
                                background: '#059669', color: '#fff', cursor: 'pointer',
                                fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6,
                            }}
                        >
                            <i className="fe-check-square" style={{ fontSize: 14 }} />
                            Settle All Pending
                            <span style={{
                                background: 'rgba(255,255,255,0.25)', borderRadius: 20,
                                padding: '1px 8px', fontSize: 11,
                            }}>
                                {pendingBookings.length}
                            </span>
                        </button>
                    )}
                    <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/settlement')}>
                        ← Back to list
                    </button>
                </div>
            </div>

            {/* ── Tutor Profile Card ── */}
            <div className="card border shadow-sm mb-3" style={{ borderRadius: 12 }}>
                <div className="card-body d-flex align-items-center gap-3 p-4">
                    {tutor.profile_picture ? (
                        <img
                            src={tutor.profile_picture}
                            alt="Profile"
                            onError={e => { e.currentTarget.onerror = null; e.currentTarget.src = process.env.PUBLIC_URL + '/assets/avatar.png' }}
                            style={{ width: 60, height: 60, borderRadius: '50%', objectFit: 'cover', border: '2px solid #e9ecef', flexShrink: 0 }}
                        />
                    ) : (
                        <div style={{
                            width: 60, height: 60, borderRadius: '50%', background: '#e8f0fe',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 20, fontWeight: 600, color: '#1a5ccf', flexShrink: 0,
                        }}>
                            {initials}
                        </div>
                    )}
                    <div className="flex-grow-1">
                        <h5 className="mb-1 fw-semibold">{displayName}</h5>
                        <div className="d-flex gap-3 flex-wrap" style={{ fontSize: 13, color: '#6c757d' }}>
                            {tutor.email && <span><i className="fe-mail me-1" />{tutor.email}</span>}
                            {tutor.phone_number && <span><i className="fe-phone me-1" />{tutor.country_code} {tutor.phone_number}</span>}
                            {tutor.username && <span><i className="fe-user me-1" />@{tutor.username}</span>}
                        </div>
                    </div>
                    <span className="badge" style={{ background: '#d1fae5', color: '#065f46', fontSize: 12 }}>Active Tutor</span>
                </div>
            </div>

            {/* ── Session Count Metrics ── */}
            <div className="row g-3 mb-3">
                {[
                    { label: 'Total Bookings', value: summary.total_bookings, icon: 'fe-book-open', color: '#3b82f6' },
                    { label: 'Total Sessions', value: summary.total_sessions, icon: 'fe-calendar', color: '#8b5cf6' },
                    { label: 'Completed', value: summary.total_completed, icon: 'fe-check-circle', color: '#10b981' },
                    { label: 'Settled', value: summary.total_settled, icon: 'fe-check-square', color: '#059669' },
                    { label: 'Pending Payout', value: summary.total_pending_payout, icon: 'fe-clock', color: '#f59e0b' },
                ].map(({ label, value, icon, color }) => (
                    <div key={label} className="col-6 col-md-4 col-lg">
                        <div className="card border-0 h-100" style={{ background: '#f8f9fa', borderRadius: 10 }}>
                            <div className="card-body p-3">
                                <div className="d-flex align-items-center gap-2 mb-2">
                                    <i className={icon} style={{ fontSize: 14, color }} />
                                    <span className="text-muted" style={{ fontSize: 11 }}>{label}</span>
                                </div>
                                <p className="mb-0 fw-semibold" style={{ fontSize: 22 }}>{value ?? 0}</p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* ── Financial Summary Cards ── */}
            <div className="row g-3 mb-3">
                {[
                    { label: 'Total Net Received', value: summary.total_net_received, bg: '#f0fdf4', color: '#065f46' },
                    { label: 'Total Amount Settled', value: summary.total_amount_settled, bg: '#eff6ff', color: '#1d4ed8' },
                    { label: 'Balance Due', value: summary.total_balance_due, bg: '#fef2f2', color: '#dc2626' },
                ].map(({ label, value, bg, color }) => (
                    <div key={label} className="col-md-4">
                        <div className="card border-0 h-100" style={{ background: bg, borderRadius: 10 }}>
                            <div className="card-body p-3">
                                <p className="text-muted mb-1" style={{ fontSize: 12 }}>{label}</p>
                                <p className="mb-0 fw-semibold" style={{ fontSize: 20, color }}>{fmt(value)}</p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* ── Settlement Progress ── */}
            <div className="card border shadow-sm mb-3" style={{ borderRadius: 12 }}>
                <div className="card-body p-4">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <p className="text-uppercase text-muted fw-semibold mb-0" style={{ fontSize: 11, letterSpacing: '0.05em' }}>
                            Settlement Progress
                        </p>
                        <div className="d-flex gap-3 align-items-center">
                            {summary.last_settlement_date && (
                                <span className="text-muted" style={{ fontSize: 12 }}>
                                    Last settled: <strong>{fmtShortDate(summary.last_settlement_date)}</strong>
                                </span>
                            )}
                            <span className="fw-semibold" style={{ fontSize: 14, color: progressColor }}>
                                {settlementProgress}%
                            </span>
                        </div>
                    </div>
                    <div className="progress" style={{ height: 10, borderRadius: 6 }}>
                        <div
                            className="progress-bar"
                            role="progressbar"
                            style={{ width: `${settlementProgress}%`, borderRadius: 6, background: progressColor }}
                        />
                    </div>
                    <div className="d-flex justify-content-between mt-2" style={{ fontSize: 12, color: '#6c757d' }}>
                        <span>{summary.total_settled} sessions settled</span>
                        <span>{summary.total_sessions} total sessions</span>
                    </div>
                </div>
            </div>

            {/* ── Bookings Detail Table ── */}
            <div className="card border shadow-sm mb-3" style={{ borderRadius: 12 }}>
                <div className="card-body p-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <p className="text-uppercase text-muted fw-semibold mb-0" style={{ fontSize: 11, letterSpacing: '0.05em' }}>
                            Bookings Detail ({bookings_detail.length})
                        </p>
                        {hasAnyPending && (
                            <span style={{ fontSize: 12, color: '#f59e0b' }}>
                                <i className="fe-clock me-1" />
                                {pendingBookings.length} booking{pendingBookings.length > 1 ? 's' : ''} with pending payout
                            </span>
                        )}
                    </div>

                    {bookings_detail.length === 0 ? (
                        <div className="text-center py-4">
                            <i className="fe-inbox" style={{ fontSize: 36, color: '#adb5bd' }} />
                            <p className="text-muted mt-2 mb-0" style={{ fontSize: 13 }}>No booking records found</p>
                        </div>
                    ) : (
                        <div style={scrollTableWrapper}>
                            <table className="table table-hover mb-0" style={{ fontSize: 12, minWidth: 1260 }}>
                                <thead style={{ position: 'sticky', top: 0, background: '#f8f9fa', zIndex: 1 }}>
                                    <tr>
                                        {[
                                            '#', 'Order ID', 'Type', 'Order Status', 'Payment Status',
                                            'Total Sess.', 'Completed', 'Settled', 'Pending', 'Cancelled', 'Pend. Payout',
                                            'Price/Session', 'Gross', 'Txn Fee', 'Admin Fee', 'Net Received', 'Settled Amt', 'Balance Due',
                                            'Purchased At', 'Action',
                                        ].map(h => (
                                            <th key={h} className="border-0 text-muted fw-semibold py-2 px-3"
                                                style={{ fontSize: 10, letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>
                                                {h.toUpperCase()}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {bookings_detail.map((row, idx) => {
                                        const orderBadge = statusBadge(row.order_status)
                                        const payBadge = statusBadge(row.order_payment_status)
                                        const pendingPayout = row.sessions_pending_payout ?? 0
                                        const isFullySettled = pendingPayout === 0 &&
                                            (row.sessions_completed ?? 0) > 0

                                        // Session dots for pack types — up to 10 shown
                                        const isPack = row.session_type !== 'single'
                                        const totalDots = Math.min(row.total_sessions ?? 0, 10)
                                        const completedCount = row.sessions_completed ?? 0
                                        const settledCount = row.sessions_settled ?? 0

                                        return (
                                            <tr key={row.booking_order_id ?? idx}
                                                style={{ background: pendingPayout > 0 ? '#fffbeb' : 'transparent' }}>
                                                <td className="py-2 px-3 text-muted">{idx + 1}</td>
                                                <td className="py-2 px-3 fw-medium" style={{ whiteSpace: 'nowrap' }}>
                                                    {row.order_id ? `#${row.order_id}` : row.booking_order_id ? `#${row.booking_order_id}` : '—'}
                                                </td>
                                                <td className="py-2 px-3" style={{ textTransform: 'capitalize', whiteSpace: 'nowrap' }}>
                                                    {(row.session_type || '—').replace(/_/g, ' ')}
                                                </td>
                                                <td className="py-2 px-3">
                                                    <span className="badge" style={{ background: orderBadge.bg, color: orderBadge.color, textTransform: 'capitalize', fontSize: 11 }}>
                                                        {row.order_status || '—'}
                                                    </span>
                                                </td>
                                                <td className="py-2 px-3">
                                                    <span className="badge" style={{ background: payBadge.bg, color: payBadge.color, textTransform: 'capitalize', fontSize: 11 }}>
                                                        {row.order_payment_status || '—'}
                                                    </span>
                                                </td>

                                                {/* Total sessions — with mini dot bar for packs */}
                                                <td className="py-2 px-3 text-center">
                                                    <div>{row.total_sessions ?? '—'}</div>
                                                    {isPack && totalDots > 0 && (
                                                        <div style={{ display: 'flex', gap: 2, marginTop: 4, justifyContent: 'center' }}>
                                                            {Array.from({ length: totalDots }).map((_, i) => (
                                                                <span key={i} style={{
                                                                    width: 7, height: 7, borderRadius: '50%',
                                                                    background: i < settledCount
                                                                        ? '#1d4ed8'
                                                                        : i < completedCount
                                                                            ? '#f59e0b'
                                                                            : '#e5e7eb',
                                                                    flexShrink: 0,
                                                                    title: i < settledCount ? 'Settled' : i < completedCount ? 'Completed, not settled' : 'Not done',
                                                                }} />
                                                            ))}
                                                        </div>
                                                    )}
                                                </td>

                                                <td className="py-2 px-3 text-center" style={{ color: '#059669' }}>{row.sessions_completed ?? '—'}</td>
                                                <td className="py-2 px-3 text-center" style={{ color: '#1d4ed8' }}>{row.sessions_settled ?? '—'}</td>
                                                <td className="py-2 px-3 text-center" style={{ color: '#92400e' }}>{row.sessions_pending ?? '—'}</td>
                                                <td className="py-2 px-3 text-center" style={{ color: '#dc2626' }}>{row.sessions_cancelled ?? '—'}</td>
                                                <td className="py-2 px-3 text-center">
                                                    {pendingPayout > 0 ? (
                                                        <span style={{
                                                            background: '#fef3c7', color: '#92400e',
                                                            borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 600,
                                                        }}>
                                                            {pendingPayout}
                                                        </span>
                                                    ) : (
                                                        <span style={{ color: '#6c757d' }}>0</span>
                                                    )}
                                                </td>

                                                <td className="py-2 px-3" style={{ whiteSpace: 'nowrap' }}>{fmt(row.price_per_session)}</td>
                                                <td className="py-2 px-3" style={{ whiteSpace: 'nowrap' }}>{fmt(row.gross_amount)}</td>
                                                <td className="py-2 px-3" style={{ whiteSpace: 'nowrap', color: '#dc2626' }}>- {fmt(row.transaction_fee)}</td>
                                                <td className="py-2 px-3" style={{ whiteSpace: 'nowrap', color: '#dc2626' }}>- {fmt(row.admin_fee)}</td>
                                                <td className="py-2 px-3 fw-semibold" style={{ whiteSpace: 'nowrap', color: '#065f46' }}>{fmt(row.net_amount_received)}</td>
                                                <td className="py-2 px-3" style={{ whiteSpace: 'nowrap', color: '#1d4ed8' }}>{fmt(row.amount_settled)}</td>
                                                <td className="py-2 px-3" style={{ whiteSpace: 'nowrap', color: row.balance_due > 0 ? '#dc2626' : '#6c757d' }}>
                                                    {fmt(row.balance_due)}
                                                </td>
                                                <td className="py-2 px-3 text-muted" style={{ whiteSpace: 'nowrap' }}>{fmtDate(row.purchased_at)}</td>

                                                {/* ── Action column ── */}
                                                <td className="py-2 px-3" style={{ whiteSpace: 'nowrap' }}>
                                                    {isFullySettled && pendingPayout === 0 ? (
                                                        <span style={{
                                                            background: '#d1fae5', color: '#065f46',
                                                            borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 500,
                                                        }}>
                                                            ✓ Settled
                                                        </span>
                                                    ) : pendingPayout === 0 ? (
                                                        <span style={{ color: '#adb5bd', fontSize: 12 }}>—</span>
                                                    ) : (
                                                        <button
                                                            onClick={() => setSettleModal(row)}
                                                            disabled={settleLoading}
                                                            style={{
                                                                padding: '4px 14px', borderRadius: 8,
                                                                border: '1px solid #1d4ed8',
                                                                background: '#eff6ff', color: '#1d4ed8',
                                                                cursor: 'pointer', fontSize: 12, fontWeight: 500,
                                                                whiteSpace: 'nowrap',
                                                            }}
                                                        >
                                                            Settle
                                                            {pendingPayout > 0 && ` (${pendingPayout})`}
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Dot legend */}
                    {bookings_detail.some(b => b.session_type !== 'single') && (
                        <div style={{ display: 'flex', gap: 16, marginTop: 12, fontSize: 11, color: '#6c757d', flexWrap: 'wrap' }}>
                            {[
                                { color: '#1d4ed8', label: 'Settled' },
                                { color: '#f59e0b', label: 'Completed, awaiting payout' },
                                { color: '#e5e7eb', label: 'Not yet conducted' },
                            ].map(({ color, label }) => (
                                <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
                                    {label}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* ── Settlements Table ── */}
            <div className="card border shadow-sm mb-3" style={{ borderRadius: 12 }}>
                <div className="card-body p-4">
                    <p className="text-uppercase text-muted fw-semibold mb-3" style={{ fontSize: 11, letterSpacing: '0.05em' }}>
                        Settlement Records ({settlements.length})
                    </p>

                    {settlements.length === 0 ? (
                        <div className="text-center py-4">
                            <i className="fe-inbox" style={{ fontSize: 36, color: '#adb5bd' }} />
                            <p className="text-muted mt-2 mb-0" style={{ fontSize: 13 }}>No settlement records found</p>
                        </div>
                    ) : (
                        <div style={scrollTableWrapper}>
                            <table className="table table-hover mb-0" style={{ fontSize: 13, minWidth: 800 }}>
                                <thead style={{ position: 'sticky', top: 0, background: '#f8f9fa', zIndex: 1 }}>
                                    <tr>
                                        {['#', 'Order ID', 'Amount', 'Status', 'Sessions', 'Settled At', 'Note'].map(h => (
                                            <th key={h} className="border-0 text-muted fw-semibold py-2 px-3"
                                                style={{ fontSize: 10, letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>
                                                {h.toUpperCase()}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {settlements.map((s, idx) => {
                                        const badge = statusBadge(s.status)
                                        return (
                                            <tr key={s.id ?? idx}>
                                                <td className="py-2 px-3 text-muted">{idx + 1}</td>
                                                <td className="py-2 px-3 fw-medium">
                                                    {s.booking_order_id ? `#${s.booking_order_id}` : '—'}
                                                </td>
                                                <td className="py-2 px-3 fw-semibold" style={{ color: '#065f46', whiteSpace: 'nowrap' }}>
                                                    {fmt(s.amount_settled)}
                                                </td>
                                                <td className="py-2 px-3">
                                                    <span className="badge" style={{ background: badge.bg, color: badge.color, textTransform: 'capitalize', fontSize: 11, padding: '4px 10px' }}>
                                                        {s.status || '—'}
                                                    </span>
                                                </td>
                                                <td className="py-2 px-3">
                                                    {s.sessions_settled != null
                                                        ? `${s.sessions_settled}${s.total_sessions ? ` / ${s.total_sessions}` : ''}`
                                                        : '—'}
                                                </td>
                                                <td className="py-2 px-3 text-muted" style={{ whiteSpace: 'nowrap' }}>
                                                    {fmtDate(s.settled_at || s.createdAt)}
                                                </td>
                                                <td className="py-2 px-3 text-muted" style={{ maxWidth: 200 }}>
                                                    <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {s.notes || '—'}
                                                    </span>
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* ── Modals ── */}
            {settleModal && (
                <SettleModal
                    booking={settleModal}
                    onClose={() => setSettleModal(null)}
                    onConfirm={handleSettleConfirm}
                    loading={!!settleLoading}
                />
            )}

            {settleAllModal && (
                <SettleAllModal
                    pendingBookings={pendingBookings}
                    onClose={() => setSettleAllModal(false)}
                    onConfirm={handleSettleAllConfirm}
                    loading={!!settleLoading}
                />
            )}

        </div>
    )
}

export default ViewSettlementDetail